import React from "react";

/**
 * Top-level error boundary: converts an uncaught render/lifecycle error
 * anywhere in the tree into a visible message instead of a white screen.
 * (Error boundaries must be class components — there is no hook API.)
 *
 * Note: this catches React render-phase errors only. Errors in event
 * handlers, async code, and workers don't reach a boundary by design.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] uncaught render error:", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error?.message || String(this.state.error);
    return (
      <div style={styles.container} role="alert">
        <div style={styles.card}>
          <h2 style={styles.title}>WaveExplorer hit an unexpected error</h2>
          <p style={styles.text}>
            Reloading usually recovers. Unsaved analysis settings may be
            lost — saved templates are unaffected.
          </p>
          <pre style={styles.detail}>{message}</pre>
          <button
            style={styles.button}
            onClick={() => window.location.reload()}
          >
            Reload WaveExplorer
          </button>
        </div>
      </div>
    );
  }
}

// Inline styles: the boundary must render even if stylesheets or the
// theme provider are what broke.
const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#666",
    fontFamily: "sans-serif",
  },
  card: {
    background: "#b3b3b3",
    borderRadius: "8px",
    padding: "2rem",
    maxWidth: "34rem",
    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
  },
  title: { margin: "0 0 0.75rem" },
  text: { margin: "0 0 1rem" },
  detail: {
    background: "#8f8f8f",
    borderRadius: "4px",
    padding: "0.75rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.8rem",
    maxHeight: "10rem",
    overflow: "auto",
  },
  button: {
    padding: "0.5rem 1.25rem",
    border: "none",
    borderRadius: "4px",
    background: "#1976d2",
    color: "#fff",
    fontSize: "1rem",
    cursor: "pointer",
  },
};

export default ErrorBoundary;
